/**
 * Addon Actions Hook
 *
 * Centralized hook for addon action handlers (archive, unarchive, activate).
 * Provides memoized action handlers to prevent recreation on every render.
 */

import { ENTITY_STATUS } from '@monorepo/shared/schemas/common'
import { useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import type { Plan } from '../types/addon.types'
import { useUpdateAddonStatus } from './useAddons'

export interface AddonActionHandlers {
  handleEdit: (addon: Plan) => void
  handleArchive: (addon: Plan) => Promise<void>
  handleUnarchive: (addon: Plan) => Promise<void>
  handleActivate: (addon: Plan) => Promise<void>
}

/**
 * Hook that provides memoized addon action handlers
 */
export function useAddonActions(): AddonActionHandlers {
  const navigate = useNavigate()
  const updateAddonStatus = useUpdateAddonStatus()

  const handleEdit = useCallback(
    (addon: Plan) => {
      navigate({
        to: '/catalog/add-ons/$addonId',
        params: { addonId: addon.id },
      })
    },
    [navigate]
  )

  const handleArchive = useCallback(
    async (addon: Plan) => {
      try {
        await updateAddonStatus.mutateAsync({
          id: addon.id,
          status: ENTITY_STATUS.archived,
        })
      } catch (error) {
        toast.error(extractErrorMessage(error))
        throw error
      }
    },
    [updateAddonStatus]
  )

  const handleUnarchive = useCallback(
    async (addon: Plan) => {
      try {
        await updateAddonStatus.mutateAsync({
          id: addon.id,
          status: ENTITY_STATUS.draft,
        })
      } catch (error) {
        toast.error(extractErrorMessage(error))
        throw error
      }
    },
    [updateAddonStatus]
  )

  const handleActivate = useCallback(
    async (addon: Plan) => {
      try {
        await updateAddonStatus.mutateAsync({
          id: addon.id,
          status: ENTITY_STATUS.active,
        })
      } catch (error) {
        toast.error(extractErrorMessage(error))
        throw error
      }
    },
    [updateAddonStatus]
  )

  return {
    handleEdit,
    handleArchive,
    handleUnarchive,
    handleActivate,
  }
}
